import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import dynamicAPI from "../api/dynamic-API";
import {
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

const UserRoles = () => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [formData, setFormData] = useState({
    user: "",
    role: "",
  });

  const queryClient = useQueryClient();

  // Fetch user roles with pagination and search
  const { data: userRoles, isLoading } = useQuery(
    ["userRoles", page, searchQuery],
    () =>
      dynamicAPI(
        "get",
        `/api/auth/user-roles?page=${page}&q=${searchQuery}&limit=25`,
        {},
      ),
    { keepPreviousData: true },
  );

  // Fetch users for the select dropdown
  const { data: users } = useQuery(
    "users",
    () => dynamicAPI("get", "/api/auth/users", {}),
    { keepPreviousData: true },
  );

  // Fetch roles for the select dropdown
  const { data: roles } = useQuery(
    "roles",
    () => dynamicAPI("get", "/api/auth/roles", {}),
    { keepPreviousData: true },
  );

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !userRoles?.data) return userRoles?.data;

    const sorted = [...userRoles.data].sort((a, b) => {
      const aValue =
        a.user[sortConfig.key] || a.role[sortConfig.key] || a[sortConfig.key];
      const bValue =
        b.user[sortConfig.key] || b.role[sortConfig.key] || b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [userRoles?.data, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <div className="w-4 h-4" />;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUpIcon className="w-4 h-4" />
    ) : (
      <ChevronDownIcon className="w-4 h-4" />
    );
  };

  const createUserRole = useMutation(
    (data) => dynamicAPI("post", "/api/auth/user-roles", data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("userRoles");
        setIsModalOpen(false);
        resetForm();
      },
      onError: (error) => {
        alert(error.response?.data?.error || "An error occurred");
      },
    },
  );

  const updateUserRole = useMutation(
    (data) => dynamicAPI("put", `/api/auth/user-roles/${data._id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("userRoles");
        setIsModalOpen(false);
        resetForm();
      },
      onError: (error) => {
        alert(error.response?.data?.error || "An error occurred");
      },
    },
  );

  const deleteUserRole = useMutation(
    (id) => dynamicAPI("delete", `/api/auth/user-roles/${id}`, {}),
    {
      onSuccess: () => queryClient.invalidateQueries("userRoles"),
      onError: (error) => {
        alert(error.response?.data?.error || "An error occurred");
      },
    },
  );

  const resetForm = () => {
    setFormData({
      user: "",
      role: "",
    });
    setEditingUserRole(null);
  };

  const handleEdit = (userRole) => {
    setEditingUserRole(userRole);
    setFormData({
      user: userRole.user._id,
      role: userRole.role._id,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingUserRole) {
      updateUserRole.mutate({ ...formData, _id: editingUserRole._id });
    } else {
      createUserRole.mutate(formData);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user role?")) {
      deleteUserRole.mutate(id);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Roles Management</h1>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Assign Role
        </button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by role name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort("email")}
              >
                <div className="flex items-center space-x-1">
                  <span>User Email</span>
                  {getSortIcon("email")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort("name")}
              >
                <div className="flex items-center space-x-1">
                  <span>Role Name</span>
                  {getSortIcon("name")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort("type")}
              >
                <div className="flex items-center space-x-1">
                  <span>Role Type</span>
                  {getSortIcon("type")}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData?.map((userRole) => (
              <tr key={userRole._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {userRole.user.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {userRole.role.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      userRole.role.type === "SUPER_ADMIN"
                        ? "bg-red-100 text-red-800"
                        : userRole.role.type === "ADMIN"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {userRole.role.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(userRole)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(userRole._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {userRoles?.pages > 1 && (
        <div className="mt-4 flex justify-center space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 bg-white rounded-md border border-gray-300">
            Page {page} of {userRoles.pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(userRoles.pages, p + 1))}
            disabled={page === userRoles.pages}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingUserRole ? "Edit User Role" : "Assign New Role"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  User
                </label>
                <select
                  value={formData.user}
                  onChange={(e) =>
                    setFormData({ ...formData, user: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a user</option>
                  {users?.data?.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a role</option>
                  {roles?.data?.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.name} ({role.type})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {editingUserRole ? "Update" : "Assign"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRoles;
